import { useMemo } from "react";
import { config } from "../config";

/**
 * Custom hook to extract and process user account information from MSAL.
 *
 * Returns:
 *   userName       - user's email / UPN
 *   nameParts      - display name split into parts
 *   roles          - full array of Azure AD roles (sent in Roles header)
 *   userRole       - highest-privilege system role ("technical_lead", etc.)
 *   project        - PRIMARY project/team role (first match), null for admins
 *   projectRoles   - ALL project/team roles the user holds
 *
 * Multi-role behaviour:
 *   A user with ["payments-and-cards", "technical_lead"] gets:
 *     userRole     = "technical_lead"  (highest system role)
 *     project      = "payments-and-cards"  (primary team)
 *     projectRoles = ["payments-and-cards"]  (all teams)
 *
 *   A user with ["technical_lead", "everyday-banking", "payments-and-cards"] gets:
 *     userRole     = "technical_lead"
 *     project      = "everyday-banking"
 *     projectRoles = ["everyday-banking", "payments-and-cards"]
 *     → can access secrets from BOTH teams
 */
function useUserAccount(msalInstance) {
  const account = msalInstance.getActiveAccount();

  const { userName, nameParts, roles, userRole, project, projectRoles } = useMemo(() => {
    if (!account) {
      return { userName: null, nameParts: [], roles: [], userRole: null, project: null, projectRoles: [] };
    }

    const userName  = account.username || "";
    const nameParts = (account.name || "").split(" ");
    const allRoles  = account.idTokenClaims?.roles || [];

    const adminRoles    = config.rbac.adminRoles    || [];
    const elevatedRoles = config.rbac.elevatedRoles || [];
    const knownTeams    = config.rbac.projectTeams  || [];
    const allSystemRoles = new Set([...adminRoles, ...elevatedRoles]);

    // ── Effective (highest-privilege) system role ──────────────────────────
    let userRole = null;
    userRole = allRoles.find((r) => adminRoles.includes(r)) ?? null;
    if (!userRole) userRole = allRoles.find((r) => elevatedRoles.includes(r)) ?? null;
    if (!userRole) userRole = allRoles.find((r) => !allSystemRoles.has(r)) ?? null;

    // ── Project/team roles — collect ALL matches ───────────────────────────
    const isAdmin = adminRoles.includes(userRole);

    let projectRoles = [];
    if (!isAdmin) {
      if (knownTeams.length > 0) {
        // Only roles that are explicitly listed as project teams
        projectRoles = allRoles.filter((r) => knownTeams.includes(r));
      } else {
        // Fallback: any non-system role
        projectRoles = allRoles.filter((r) => !allSystemRoles.has(r));
      }
    }

    const project = projectRoles[0] ?? null;

    return { userName, nameParts, roles: allRoles, userRole, project, projectRoles };
  }, [account]);

  return { userName, nameParts, roles, userRole, project, projectRoles };
}

export default useUserAccount;